import type { RaceView } from './raceView';

/** What the lobby has already announced to the host (docs/FRIENDS.md, step 1). */
export interface LobbyHeard {
  /** The friends whose arrival was announced (one in a room of two; up to three in a party). */
  friends: readonly string[];
  /** START was announced as ready (everyone in and ready). */
  ready: boolean;
}

export const NOTHING_HEARD: LobbyHeard = { friends: [], ready: false };

/**
 * The host's cues for a new view of the room: a honk when a friend arrives (once per friend, so
 * a reconnect's fresh room state after the app was away still honks, but only the first time),
 * and a chime when START becomes ready (two in, or in a party two or more, all ready). Only the
 * host (seat 0) waiting in the room hears either. Pure.
 */
export function lobbyCues(
  heard: LobbyHeard,
  view: RaceView,
): { honk: boolean; chime: boolean; heard: LobbyHeard } {
  const waiting = view.you === 0 && view.line !== 'closed' && !view.start && !view.result;
  const here = waiting ? view.players.filter((p) => p.seat !== 0 && p.here) : [];
  const enough = view.seats > 2 ? view.players.length >= 2 : view.players.length === 2;
  const ready = waiting && enough && view.players.every((p) => p.ready && p.here);
  const honk = here.some((p) => !heard.friends.includes(p.player));
  const chime = ready && !heard.ready;
  // A friend who left (or went away) is forgotten only once gone from the room, so a quick blip of
  // their line does not honk again; someone new does.
  const inRoom = new Set(view.players.map((p) => p.player));
  const friends = [
    ...new Set([...heard.friends.filter((f) => inRoom.has(f)), ...here.map((p) => p.player)]),
  ];
  return { honk, chime, heard: { friends, ready } };
}
